import React, { useState, useRef, useEffect, useCallback } from "react";
import { DropdownContainer, DropdownButton, DropdownMenu, DropdownItem } from "./styles";

export interface DropdownItemType {
  id: number;
  name: string;
  icon?: string;
  glosa?: string;
}

interface DropdownProps {
  items: DropdownItemType[];
  value: string;
  onChange: (itemName: string) => void;
  placeholder?: string;
}

export function Dropdown({
  items,
  value,
  onChange,
  placeholder = "Selecione uma opção"
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const selectedItem = items.find((item) => item.name === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  useEffect(() => {
    if (isOpen) {
      const currentIndex = items.findIndex((item) => item.name === value);
      setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
      menuRef.current?.focus();
    }
  }, [isOpen, items, value]);

  const openDropdown = useCallback(() => setIsOpen(true), []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
    buttonRef.current?.focus();
  }, []);

  const selectItem = useCallback((itemName: string) => {
    onChange(itemName);
    closeDropdown();
  }, [onChange, closeDropdown]);

  const handleButtonKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case "Enter":
      case " ":
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        openDropdown();
        break;
    }
  }, [openDropdown]);

  const handleMenuKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setFocusedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        event.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (focusedIndex >= 0 && items[focusedIndex]) {
          selectItem(items[focusedIndex].name);
        }
        break;
      case "Escape":
      case "Tab":
        event.preventDefault();
        closeDropdown();
        break;
      case "Home":
        event.preventDefault();
        setFocusedIndex(0);
        break;
      case "End":
        event.preventDefault();
        setFocusedIndex(items.length - 1);
        break;
    }
  }, [items, focusedIndex, selectItem, closeDropdown]);

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton
        ref={buttonRef}
        onClick={() => (isOpen ? closeDropdown() : openDropdown())}
        onKeyDown={handleButtonKeyDown}
        isOpen={isOpen}
        isEmpty={!value}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        tabIndex={5}
      >
        <span>{selectedItem?.name || placeholder}</span>
        <span className="material-icons-outlined arrow">expand_more</span>
      </DropdownButton>

      {isOpen && (
        <DropdownMenu
          ref={menuRef}
          role="listbox"
          tabIndex={-1}
          onKeyDown={handleMenuKeyDown}
          aria-activedescendant={
            focusedIndex >= 0 ? `dropdown-item-${items[focusedIndex]?.id}` : undefined
          }
        >
          {items.map((item, index) => (
            <DropdownItem
              key={item.id}
              id={`dropdown-item-${item.id}`}
              ref={(el: HTMLDivElement | null) => { itemRefs.current[index] = el; }}
              role="option"
              aria-selected={value === item.name}
              isSelected={value === item.name}
              data-focused={focusedIndex === index ? "true" : undefined}
              onClick={() => selectItem(item.name)}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <span>{item.name}</span>
            </DropdownItem>
          ))}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
}